import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    RefreshCw,
    ArrowLeft,
    Camera,
    ChevronDown,
    ChevronRight,
    ShieldAlert,
    Bug,
    Activity,
    UploadCloud,
    CreditCard,
    Mail,
    MessageSquare,
    Globe,
    CheckCircle2,
    Send,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import PublicLayout from "@/components/layout/PublicLayout";
import { adminIssuesSnapshot } from "@/lib/api";
import PinQualityPanel from "@/components/admin/PinQualityPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";


// Send-test-digest button. Solves "I haven't received any emails yet"
// by force-firing one digest to the requesting admin RIGHT NOW with
// the current state of every signal source. Bypasses the buffer's
// 5-min coalescing window via the backend's `flush_now()`. Real
// alerts still fire on transitions only — this is just a deliverability
// + format check.
function SendTestDigestButton() {
    const [sending, setSending] = useState(false);
    const handleSend = async () => {
        const ok = window.confirm(
            "Send a TEST ops digest to your admin email right now?\n\n"
            + "Includes current preflight + pin quality status. "
            + "Real alerts only fire on transitions (green→red), so "
            + "this test confirms Resend deliverability + format."
        );
        if (!ok) return;
        setSending(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/issues/send-test-digest`,
                {},
                { withCredentials: true },
            );
            toast.success(
                `Test digest sent to ${data?.sent_to || "your inbox"} — check Resend dashboard if not delivered in ~30s.`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Failed to send test digest"
            );
        } finally {
            setSending(false);
        }
    };
    return (
        <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="bl-btn-ghost text-xs gap-1 disabled:opacity-50"
            data-testid="issues-send-test-digest"
            title="Force-fire one ops digest to your inbox now — bypasses the 5-min coalescing buffer"
        >
            {sending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
                <Send className="h-3 w-3" />
            )}
            Send test digest
        </button>
    );
}

const SECTION_ICONS = {
    tripwires: ShieldAlert,
    backend_errors: Bug,
    background_task_errors: Activity,
    failed_uploads: UploadCloud,
    failed_payments: CreditCard,
    failed_emails: Mail,
    user_reports: MessageSquare,
    client_js_errors: Globe,
};

function fmtTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString();
    } catch {
        /* non-critical */
        return iso;
    }
}

function CountPill({ n, testid }) {
    const tone =
        n === 0
            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
            : n < 5
            ? "bg-amber-50 text-amber-800 border-amber-200"
            : "bg-rose-50 text-rose-800 border-rose-200";
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 text-xs font-semibold rounded-full border ${tone}`}
        >
            {n}
        </span>
    );
}

function SectionRow({ section, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);
    const Icon = SECTION_ICONS[section.id] || AlertTriangle;
    const empty = section.count === 0;
    return (
        <div
            className="bl-card overflow-hidden"
            data-testid={`issues-section-${section.id}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full px-5 py-4 flex items-center gap-3 hover:bg-bl-bg/40 transition-colors text-left"
                data-testid={`issues-section-toggle-${section.id}`}
            >
                {open ? (
                    <ChevronDown className="h-4 w-4 text-bl-muted" />
                ) : (
                    <ChevronRight className="h-4 w-4 text-bl-muted" />
                )}
                <Icon
                    className={`h-4 w-4 ${
                        empty ? "text-emerald-600" : "text-rose-600"
                    }`}
                />
                <div className="flex-1">
                    <div className="font-display font-semibold text-sm text-bl">
                        {section.title}
                    </div>
                </div>
                <CountPill
                    n={section.count}
                    testid={`issues-count-${section.id}`}
                />
            </button>
            {open && (
                <div className="border-t border-bl-line">
                    {empty ? (
                        <div className="px-5 py-6 text-xs text-bl-muted flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            All clear in this category.
                        </div>
                    ) : (
                        <SectionBody section={section} />
                    )}
                </div>
            )}
        </div>
    );
}

function SectionBody({ section }) {
    const items = section.items || [];
    if (section.id === "tripwires") {
        return (
            <ul className="divide-y divide-bl-line">
                {items.map((it) => (
                    <li
                        key={it.id}
                        className="px-5 py-3 text-sm"
                        data-testid={`tripwire-${it.id}`}
                    >
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-rose-600" />
                            <span className="font-medium text-bl">
                                {it.label}
                            </span>
                            <span className="text-[10px] uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                                {it.status}
                            </span>
                            <span className="ml-auto text-[11px] text-bl-muted">
                                {fmtTime(it.checked_at)}
                            </span>
                        </div>
                        {it.message && (
                            <div className="mt-1 text-xs text-bl-muted">
                                {it.message}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        );
    }
    if (
        section.id === "backend_errors" ||
        section.id === "background_task_errors"
    ) {
        return (
            <ul className="divide-y divide-bl-line">
                {items.map((it, i) => (
                    <li
                        key={`${section.id}-${i}`}
                        className="px-5 py-3 text-xs font-mono"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            {it.level && (
                                <span className="inline-block text-[10px] uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                                    {it.level}
                                </span>
                            )}
                            {it.loop && (
                                <span className="inline-block text-[10px] uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                                    {it.loop}
                                </span>
                            )}
                            {it.ts && (
                                <span className="text-[11px] text-bl-muted">
                                    {it.ts}
                                </span>
                            )}
                        </div>
                        <div className="text-bl break-words">
                            {it.summary}
                        </div>
                        {Array.isArray(it.lines) && it.lines.length > 1 && (
                            <details className="mt-2">
                                <summary className="cursor-pointer text-[11px] text-bl-muted">
                                    Show stack ({it.lines.length} lines)
                                </summary>
                                <pre className="mt-2 text-[11px] text-bl-muted whitespace-pre-wrap overflow-x-auto leading-relaxed">
                                    {it.lines.join("\n")}
                                </pre>
                            </details>
                        )}
                    </li>
                ))}
            </ul>
        );
    }
    // Generic key/value renderer for the rest of the sections.
    return (
        <ul className="divide-y divide-bl-line">
            {items.map((it, i) => (
                <li
                    key={`${section.id}-${i}`}
                    className="px-5 py-3 text-xs"
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        {it.status && (
                            <span className="inline-block text-[10px] uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                                {it.status}
                            </span>
                        )}
                        {it.kind && (
                            <span className="inline-block text-[10px] uppercase tracking-wide text-bl-muted bg-bl-line/60 px-1.5 py-0.5 rounded">
                                {it.kind}
                            </span>
                        )}
                        {it.source && (
                            <span className="inline-block text-[10px] uppercase tracking-wide text-bl-muted bg-bl-line/60 px-1.5 py-0.5 rounded">
                                {it.source}
                            </span>
                        )}
                        {it.email_type && (
                            <span className="text-bl-muted">
                                {it.email_type}
                            </span>
                        )}
                        <span className="ml-auto text-[11px] text-bl-muted">
                            {fmtTime(it.created_at)}
                        </span>
                    </div>
                    <div className="mt-1 text-bl break-words">
                        {it.message ||
                            it.error_message ||
                            it.error ||
                            it.subject ||
                            it.filename ||
                            it.url ||
                            it.recipient ||
                            it.session_id ||
                            JSON.stringify(it).slice(0, 240)}
                    </div>
                    {(it.user_id || it.email || it.user_email) && (
                        <div className="mt-1 text-[11px] text-bl-muted">
                            {it.user_email || it.email || it.user_id}
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
}

export default function AdminIssues() {
    const [snap, setSnap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [windowDays, setWindowDays] = useState(7);
    const [error, setError] = useState(null);

    const refresh = useCallback(
        async (silent = false) => {
            if (silent) setRefreshing(true);
            else setLoading(true);
            setError(null);
            try {
                const r = await adminIssuesSnapshot({
                    window_days: windowDays,
                });
                setSnap(r);
            } catch (e) {
                const msg =
                    e?.response?.data?.detail ||
                    e?.message ||
                    "Failed to load issues snapshot.";
                setError(msg);
                if (silent) toast.error(msg);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [windowDays],
    );

    useEffect(() => {
        refresh();
    }, [refresh]);

    const total = snap?.total_issues ?? 0;
    const headline =
        total === 0
            ? "All clear · no failures detected"
            : `${total} item${total === 1 ? "" : "s"} need attention`;

    return (
        <PublicLayout>
            <div className="bl-section py-10">
                <div
                    className="bl-container max-w-5xl space-y-5"
                    data-testid="admin-issues-page"
                >
                    <Link
                        to="/admin"
                        className="inline-flex items-center gap-1.5 text-xs text-bl-muted hover:text-bl transition-colors"
                        data-testid="issues-back-to-admin"
                    >
                        <ArrowLeft className="h-3 w-3" />
                        Back to Admin
                    </Link>

                    <div className="bl-card p-5 flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted flex items-center gap-2">
                                <Camera className="h-3 w-3" />
                                Issues & failures snapshot
                            </div>
                            <h1 className="font-display font-semibold text-2xl text-bl tracking-tight mt-1">
                                {loading ? "Loading…" : headline}
                            </h1>
                            <p className="text-xs text-bl-muted mt-1 max-w-2xl leading-relaxed">
                                Consolidated read-only view of every failure
                                signal across the platform — tripwires, server
                                errors, failed uploads / payments / emails,
                                support inbox, and browser JS errors. Take a
                                screenshot of this page to share for triage.
                            </p>
                            {snap?.env_label && (
                                <div className="mt-2 text-[11px] text-bl-muted">
                                    env=<b>{snap.env_label}</b> · generated{" "}
                                    {fmtTime(snap.generated_at)}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <SendTestDigestButton />
                            <select
                                value={windowDays}
                                onChange={(e) =>
                                    setWindowDays(Number(e.target.value))
                                }
                                className="text-xs px-2 py-1.5 rounded-md border border-bl bg-bl-surface"
                                data-testid="issues-window-select"
                            >
                                <option value={1}>Last 24h</option>
                                <option value={7}>Last 7 days</option>
                                <option value={14}>Last 14 days</option>
                                <option value={30}>Last 30 days</option>
                            </select>
                            <button
                                type="button"
                                onClick={() => refresh(true)}
                                className="bl-btn-ghost text-xs gap-1"
                                disabled={loading || refreshing}
                                data-testid="issues-refresh"
                            >
                                <RefreshCw
                                    className={`h-3 w-3 ${
                                        loading || refreshing
                                            ? "animate-spin"
                                            : ""
                                    }`}
                                />
                                Refresh
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div
                            className="bl-card p-4 border-rose-200 bg-rose-50/40 text-sm text-rose-800"
                            data-testid="issues-error"
                        >
                            <div className="flex items-center gap-2 font-medium">
                                <AlertTriangle className="h-4 w-4" />
                                Failed to load snapshot
                            </div>
                            <div className="mt-1 text-xs">{error}</div>
                        </div>
                    )}

                    {loading && !snap ? (
                        <div className="bl-card p-8 text-center text-sm text-bl-muted">
                            Loading snapshot…
                        </div>
                    ) : snap ? (
                        <div
                            className="space-y-3"
                            data-testid="issues-sections"
                        >
                            {(snap.sections || []).map((section) => (
                                <SectionRow
                                    key={section.id}
                                    section={section}
                                    defaultOpen={section.count > 0}
                                />
                            ))}
                            {/* Pin quality scan — deterministic Pillow QA across
                                every generated Pinterest pin. Kept at the bottom
                                of the sections since it's an on-demand scan
                                (not a continuously-updated counter). */}
                            <PinQualityPanel />
                        </div>
                    ) : null}
                </div>
            </div>
        </PublicLayout>
    );
}
